/**
 * PhysicalHealthInfoGroupField.js
 * --------------------------------
 * Purpose:
 *   Custom form field component for capturing physical health measurements
 *   (height/weight, head circumference, pulse rate, blood pressure).
 *
 * Business Context:
 *   - Medical history forms ask guardians to provide current physical measurements
 *     for a child being referred.
 *   - Each measurement category is presented as its own wizard step but all share
 *     the same question type (`physicalHealthInfoGroup`) with a `measurementType`
 *     field that controls which UI variant to render.
 *   - Measurements support dual units (metric / imperial) where applicable.
 *   - Dates are captured as month/year (not full dates) since exact day is rarely
 *     known for routine measurements.
 *   - "Not sure" pills for dates and "I'm unable to take this measurement" pills
 *     follow the same toggle-button styling used in MilestoneAgeGroupField.
 *
 * Four Measurement Types:
 *   1. height_weight   — Weight (kg or lb) + Height (cm or ft/in), each with a date
 *   2. head_circumference — Single value (cm or in) with a date
 *   3. pulse_rate      — Single value (bpm) with "unable" option and a date
 *   4. blood_pressure  — Systolic + diastolic with "unable" option and a date
 *
 * Data Shapes (stored in answers[questionId]):
 *
 *   height_weight:
 *   {
 *     weight: { value: 70, unit: 'kg', date: { month: 1, year: 2024 }, dateNotSure: false },
 *     height: { value: 175, unit: 'cm', ft: null, in: null, date: { month: 1, year: 2024 }, dateNotSure: false },
 *   }
 *
 *   head_circumference:
 *   { value: 55, unit: 'cm', date: { month: 1, year: 2024 }, dateNotSure: false }
 *
 *   pulse_rate:
 *   { value: 72, unable: false, date: { month: 1, year: 2024 }, dateNotSure: false }
 *
 *   blood_pressure:
 *   { systolic: 120, diastolic: 80, unable: false, date: { month: 1, year: 2024 }, dateNotSure: false }
 *
 * Usage in Schema:
 *   {
 *     "questionId": "height_weight",
 *     "type": "physicalHealthInfoGroup",
 *     "measurementType": "height_weight",
 *     "label": "What is their height and weight?",
 *     "required": false
 *   }
 */

import React, { useMemo, useCallback, useState } from 'react';
import { measurementProblems, MEASUREMENT_LIMITS as LIMITS } from '../utils/physicalMeasurements';

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI PRIMITIVES
// These small sub-components keep the per-measurement renderers readable.
// ─────────────────────────────────────────────────────────────────────────────

/** Month names for the date dropdown. */
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

/**
 * Generate a list of year options for the date dropdown.
 *
 * If the CYP's birth year is known, the range spans from the current year back
 * to the birth year (a measurement can't predate the child).  Otherwise falls
 * back to a sensible default of 5 years (measurements are typically recent).
 *
 * @param {number|null|undefined} birthYear - Year the CYP was born (e.g. 2018)
 */
function getYearOptions(birthYear) {
  const currentYear = new Date().getFullYear();
  // Use the CYP's birth year when available; otherwise default to 5-year lookback
  const earliest = (typeof birthYear === 'number' && birthYear > 1900)
    ? birthYear
    : currentYear - 5;
  const years = [];
  for (let y = currentYear; y >= earliest; y--) {
    years.push(y);
  }
  return years;
}

/**
 * PillButton — Reusable toggle pill button matching the RadioGroup / CheckboxGroup
 * design language (border-primary selected state, yellow indicator dot).
 */
function PillButton({ active, onClick, label, id }) {
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
        border-2 cursor-pointer select-none max-w-full text-left
        transition-all duration-200 ease-out
        focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
        ${active
          ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
        }
      `}
      aria-pressed={active}
    >
      {/* Yellow indicator dot with checkmark — mirrors RadioGroup theming */}
      <span
        className={`
          flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 relative
          transition-all duration-200 ease-out
          ${active ? 'bg-yellow-400 border-yellow-400' : 'border-gray-300 bg-white'}
        `}
        aria-hidden="true"
      >
        {active && (
          <svg viewBox="0 0 16 16" className="absolute inset-0 w-2.5 h-2.5 m-auto fill-white">
            <path d="M6.173 12.414a1 1 0 0 1-1.414 0L1.88 9.535a1 1 0 1 1 1.415-1.415l2.18 2.18 7.23-7.23a1 1 0 1 1 1.415 1.414l-8.947 8.93z"/>
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

/**
 * MonthYearDateInput — Paired month/year selects with an optional "Not sure" pill.
 *
 * Props:
 *   date       – { month: number|null, year: number|null } or null
 *   notSure    – boolean
 *   onDateChange(date)    – called with updated { month, year }
 *   onNotSureToggle()     – toggle "Not sure" state
 *   disabled   – if true, the selects are disabled (e.g. parent "unable" is active)
 *   idPrefix   – string prefix for element IDs (accessibility)
 *   dateLabel  – label text shown before the selects
 *   birthYear  – (optional) CYP birth year to constrain how far back years go
 */
function MonthYearDateInput({ date, notSure, onDateChange, onNotSureToggle, disabled, idPrefix, dateLabel, birthYear }) {
  const month = date?.month ?? '';
  const year = date?.year ?? '';
  const fieldDisabled = disabled || notSure;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <span className="text-xs font-medium text-gray-700 whitespace-nowrap">{dateLabel}</span>
      <div className="flex items-center gap-2">
        {/* Month select */}
        <select
          id={`${idPrefix}_month`}
          value={month}
          onChange={(e) => {
            const m = e.target.value === '' ? null : parseInt(e.target.value, 10);
            onDateChange({ month: m, year: date?.year ?? null });
          }}
          disabled={fieldDisabled}
          className={`
            px-2 py-1.5 text-sm border rounded-md
            focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
            ${fieldDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
              : 'bg-white text-gray-900 border-gray-300'
            }
          `}
        >
          <option value="">Month</option>
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        {/* Year select */}
        <select
          id={`${idPrefix}_year`}
          value={year}
          onChange={(e) => {
            const y = e.target.value === '' ? null : parseInt(e.target.value, 10);
            onDateChange({ month: date?.month ?? null, year: y });
          }}
          disabled={fieldDisabled}
          className={`
            px-2 py-1.5 text-sm border rounded-md
            focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
            ${fieldDisabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
              : 'bg-white text-gray-900 border-gray-300'
            }
          `}
        >
          <option value="">Year</option>
          {getYearOptions(birthYear).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
      {/* "or" divider */}
      <span className="text-xs text-gray-400 select-none">or</span>
      {/* "Not sure" pill for date */}
      <PillButton
        active={notSure}
        onClick={onNotSureToggle}
        label="Not sure"
        id={`${idPrefix}_not_sure`}
      />
    </div>
  );
}

/**
 * NumberInput — Small numeric input with consistent styling.
 */
function NumberInput({ id, value, onChange, disabled, placeholder, min, max, className = '', invalid = false, describedBy, onFocus, onBlur }) {
  return (
    <input
      type="number"
      id={id}
      min={min}
      max={max}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? describedBy : undefined}
      onFocus={onFocus}
      onBlur={onBlur}
      value={disabled ? '' : (value ?? '')}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? null : parseFloat(raw));
      }}
      disabled={disabled}
      placeholder={disabled ? '-' : (placeholder || '')}
      className={`
        w-20 px-2 py-1.5 text-sm text-center border rounded-md
        focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
        ${disabled
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
          : `bg-white text-gray-900 ${invalid ? 'border-red-600' : 'border-gray-300'}`
        }
        ${className}
      `}
    />
  );
}

/** A measurement outside the plausible range: what a right answer looks like, with the icon and the colour. */
function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="flex items-start gap-1 text-sm text-red-700 text-left">
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" className="mt-0.5 flex-shrink-0">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 4.5v4M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <span>{message}</span>
    </p>
  );
}

/**
 * Range messages wait until the person leaves the box, so typing "25" never
 * flashes an error at the "2". A value saved earlier shows its message at once.
 */
function useRangeMessages(measurementType, value, questionId) {
  const [editing, setEditing] = useState(null);
  const problems = measurementProblems(measurementType, value);
  return (key) => {
    const message = editing === key ? null : problems[key] || null;
    return {
      message,
      errorId: `${questionId}_${key}_error`,
      inputProps: {
        invalid: Boolean(message),
        describedBy: `${questionId}_${key}_error`,
        onFocus: () => setEditing(key),
        onBlur: () => setEditing(null),
      },
    };
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// MEASUREMENT-SPECIFIC RENDERERS
// Each function renders a distinct measurement layout inside a card.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * HeightWeightRenderer
 * Renders weight (kg or lb) and height (cm or ft/in), each with its own date.
 * The height_weight data shape contains two sub-objects: weight and height.
 */
function HeightWeightRenderer({ questionId, value, onChange, birthYear }) {
  // Stabilize sub-objects with useMemo so useCallback deps don't change every render
  const weight = useMemo(() => value?.weight || {}, [value?.weight]);
  const height = useMemo(() => value?.height || {}, [value?.height]);

  // ── Weight handlers ──
  const setWeight = useCallback((patch) => {
    onChange({ ...value, weight: { ...weight, ...patch } });
  }, [value, weight, onChange]);

  const handleWeightValue = useCallback((v) => setWeight({ value: v }), [setWeight]);
  const handleWeightUnit = useCallback((u) => {
    // When switching units, clear the value to avoid confusion (70 kg !== 70 lb)
    setWeight({ unit: u, value: null });
  }, [setWeight]);
  const handleWeightDate = useCallback((d) => setWeight({ date: d, dateNotSure: false }), [setWeight]);
  const handleWeightDateNotSure = useCallback(() => {
    setWeight({ dateNotSure: !weight.dateNotSure, date: !weight.dateNotSure ? null : weight.date });
  }, [setWeight, weight]);

  // ── Height handlers ──
  const setHeight = useCallback((patch) => {
    onChange({ ...value, height: { ...height, ...patch } });
  }, [value, height, onChange]);

  const handleHeightValue = useCallback((v) => setHeight({ value: v, ft: null, in: null }), [setHeight]);
  const handleHeightFt = useCallback((v) => setHeight({ ft: v, value: null }), [setHeight]);
  const handleHeightIn = useCallback((v) => setHeight({ in: v, value: null }), [setHeight]);
  const handleHeightUnit = useCallback((u) => {
    // Clear all height values when switching units
    setHeight({ unit: u, value: null, ft: null, in: null });
  }, [setHeight]);
  const handleHeightDate = useCallback((d) => setHeight({ date: d, dateNotSure: false }), [setHeight]);
  const handleHeightDateNotSure = useCallback(() => {
    setHeight({ dateNotSure: !height.dateNotSure, date: !height.dateNotSure ? null : height.date });
  }, [setHeight, height]);

  const weightUnit = weight.unit || 'kg';
  const heightUnit = height.unit || 'cm';
  const range = useRangeMessages('height_weight', value, questionId);
  const weightRange = range('weight');
  const heightRange = range('height');

  return (
    <div className="space-y-5">
      {/* ── Weight sub-section ── */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
        <p className="text-sm font-medium text-gray-900">Please enter their weight</p>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
          {/* Weight value input */}
          <NumberInput
            id={`${questionId}_weight_value`}
            value={weight.value}
            onChange={handleWeightValue}
            disabled={false}
            min={LIMITS.weight[weightUnit][0]}
            max={LIMITS.weight[weightUnit][1]}
            {...weightRange.inputProps}
          />
          {/* Unit toggle: kg or lb */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleWeightUnit('kg')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all duration-200 ease-out
                ${weightUnit === 'kg'
                  ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
                }`}
            >
              kg
            </button>
            <span className="text-xs text-gray-400 select-none">or</span>
            <button
              type="button"
              onClick={() => handleWeightUnit('lb')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all duration-200 ease-out
                ${weightUnit === 'lb'
                  ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
                }`}
            >
              lb
            </button>
          </div>
        </div>
        <FieldError id={weightRange.errorId} message={weightRange.message} />
        {/* Weight date */}
        <MonthYearDateInput
          date={weight.date}
          notSure={weight.dateNotSure || false}
          onDateChange={handleWeightDate}
          onNotSureToggle={handleWeightDateNotSure}
          disabled={false}
          idPrefix={`${questionId}_weight_date`}
          dateLabel="When was this taken?"
          birthYear={birthYear}
        />
      </div>

      {/* ── Height sub-section ── */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
        <p className="text-sm font-medium text-gray-900">Please enter their height</p>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
          {/* Height value: cm OR ft+in depending on selected unit */}
          {heightUnit === 'cm' ? (
            <NumberInput
              id={`${questionId}_height_value`}
              value={height.value}
              onChange={handleHeightValue}
              disabled={false}
              min={LIMITS.height.cm[0]}
              max={LIMITS.height.cm[1]}
              {...heightRange.inputProps}
            />
          ) : (
            <div className="flex items-center gap-1">
              <NumberInput
                id={`${questionId}_height_ft`}
                value={height.ft}
                onChange={handleHeightFt}
                disabled={false}
                placeholder="ft"
                min={0}
                max={8}
                className="w-16"
                {...heightRange.inputProps}
              />
              <span className="text-xs text-gray-500">ft</span>
              <NumberInput
                id={`${questionId}_height_in`}
                value={height.in}
                onChange={handleHeightIn}
                disabled={false}
                placeholder="in"
                min={0}
                max={11}
                className="w-16"
                {...heightRange.inputProps}
              />
              <span className="text-xs text-gray-500">in</span>
            </div>
          )}
          {/* Unit toggle: cm or ft/in */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleHeightUnit('cm')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all duration-200 ease-out
                ${heightUnit === 'cm'
                  ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
                }`}
            >
              cm
            </button>
            <span className="text-xs text-gray-400 select-none">or</span>
            <button
              type="button"
              onClick={() => handleHeightUnit('ft_in')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all duration-200 ease-out
                ${heightUnit === 'ft_in'
                  ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
                }`}
            >
              ft / in
            </button>
          </div>
        </div>
        <FieldError id={heightRange.errorId} message={heightRange.message} />
        {/* Height date */}
        <MonthYearDateInput
          date={height.date}
          notSure={height.dateNotSure || false}
          onDateChange={handleHeightDate}
          onNotSureToggle={handleHeightDateNotSure}
          disabled={false}
          idPrefix={`${questionId}_height_date`}
          dateLabel="When was this taken?"
          birthYear={birthYear}
        />
      </div>
    </div>
  );
}

/**
 * HeadCircumferenceRenderer
 * Renders a single value input (cm or in) with a date.
 * 
 * Includes an "I don't know" option for guardians who don't have this measurement.
 * This is commonly the case for head circumference which is only routinely measured
 * for children under 5. When "notSure" is selected:
 *   - The value input is disabled
 *   - The date section is hidden (no point asking when it was taken if unknown)
 *   - The question is considered complete (can proceed without value or date)
 */
function HeadCircumferenceRenderer({ questionId, value, onChange, birthYear }) {
  // Stabilize with useMemo so useCallback deps don't change every render
  const val = useMemo(() => value || {}, [value]);
  const unit = val.unit || 'cm';
  const notSure = val.notSure || false;
  const valueRange = useRangeMessages('head_circumference', val, questionId)('value');

  const handleValue = useCallback((v) => {
    // Entering a value clears the "notSure" flag
    onChange({ ...val, value: v, notSure: v != null ? false : val.notSure });
  }, [val, onChange]);
  
  const handleUnit = useCallback((u) => onChange({ ...val, unit: u, value: null }), [val, onChange]);
  
  const handleNotSureToggle = useCallback(() => {
    const nowNotSure = !notSure;
    onChange({
      ...val,
      notSure: nowNotSure,
      // Clear value when marking as not sure
      value: nowNotSure ? null : val.value,
      // Also mark date as not sure when measurement is not sure
      dateNotSure: nowNotSure ? true : val.dateNotSure,
    });
  }, [val, notSure, onChange]);
  
  const handleDate = useCallback((d) => onChange({ ...val, date: d, dateNotSure: false }), [val, onChange]);
  const handleDateNotSure = useCallback(() => {
    onChange({ ...val, dateNotSure: !val.dateNotSure, date: !val.dateNotSure ? null : val.date });
  }, [val, onChange]);

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
      <p className="text-sm font-medium text-gray-900">Please enter their head circumference</p>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
        <NumberInput
          id={`${questionId}_value`}
          value={val.value}
          onChange={handleValue}
          disabled={notSure}
          min={LIMITS.headCircumference[unit][0]}
          max={LIMITS.headCircumference[unit][1]}
          {...valueRange.inputProps}
        />
        {/* Unit toggle: cm or in */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleUnit('cm')}
            disabled={notSure}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all duration-200 ease-out
              ${notSure ? 'opacity-50 cursor-not-allowed' : ''}
              ${unit === 'cm'
                ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
              }`}
          >
            cm
          </button>
          <span className="text-xs text-gray-400 select-none">or</span>
          <button
            type="button"
            onClick={() => handleUnit('in')}
            disabled={notSure}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 transition-all duration-200 ease-out
              ${notSure ? 'opacity-50 cursor-not-allowed' : ''}
              ${unit === 'in'
                ? 'border-primary bg-primary/5 text-gray-900 shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 cursor-pointer'
              }`}
          >
            in
          </button>
        </div>
        <span className="text-xs text-gray-400 select-none">or</span>
        {/* "I don't know" pill for cases where guardian doesn't have this measurement */}
        <PillButton
          active={notSure}
          onClick={handleNotSureToggle}
          label="I don't know"
          id={`${questionId}_not_sure`}
        />
      </div>
      <FieldError id={valueRange.errorId} message={valueRange.message} />
      {/* Only show date input when user has or will provide a measurement value */}
      {!notSure && (
        <MonthYearDateInput
          date={val.date}
          notSure={val.dateNotSure || false}
          onDateChange={handleDate}
          onNotSureToggle={handleDateNotSure}
          disabled={false}
          idPrefix={`${questionId}_date`}
          dateLabel="When was this taken?"
          birthYear={birthYear}
        />
      )}
    </div>
  );
}

/**
 * PulseRateRenderer
 * Renders a BPM input with an "I'm unable to take this measurement" toggle and a date.
 * When "unable" is active, the BPM input is disabled.
 */
function PulseRateRenderer({ questionId, value, onChange, birthYear }) {
  // Stabilize with useMemo so useCallback deps don't change every render
  const val = useMemo(() => value || {}, [value]);
  const unable = val.unable || false;
  const valueRange = useRangeMessages('pulse_rate', val, questionId)('value');

  const handleValue = useCallback((v) => {
    // Entering a value clears the "unable" flag
    onChange({ ...val, value: v, unable: v != null ? false : val.unable });
  }, [val, onChange]);

  const handleUnableToggle = useCallback(() => {
    const nowUnable = !unable;
    onChange({
      ...val,
      unable: nowUnable,
      // Clear value when marking as unable
      value: nowUnable ? null : val.value,
    });
  }, [val, unable, onChange]);

  const handleDate = useCallback((d) => onChange({ ...val, date: d, dateNotSure: false }), [val, onChange]);
  const handleDateNotSure = useCallback(() => {
    onChange({ ...val, dateNotSure: !val.dateNotSure, date: !val.dateNotSure ? null : val.date });
  }, [val, onChange]);

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
      <p className="text-sm font-medium text-gray-900">Please enter their pulse rate in beats per minute</p>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2">
          <NumberInput
            id={`${questionId}_value`}
            value={val.value}
            onChange={handleValue}
            disabled={unable}
            min={LIMITS.pulse[0]}
            max={LIMITS.pulse[1]}
            {...valueRange.inputProps}
          />
          <span className="text-xs text-gray-500">bpm</span>
        </div>
        {/* "or" divider */}
        <span className="text-xs text-gray-400 select-none">or</span>
        {/* "Unable" pill */}
        <PillButton
          active={unable}
          onClick={handleUnableToggle}
          label="I'm unable to take this measurement"
          id={`${questionId}_unable`}
        />
      </div>
      <FieldError id={valueRange.errorId} message={valueRange.message} />
      {/* Date */}
      <MonthYearDateInput
        date={val.date}
        notSure={val.dateNotSure || false}
        onDateChange={handleDate}
        onNotSureToggle={handleDateNotSure}
        disabled={unable}
        idPrefix={`${questionId}_date`}
        dateLabel="When was this taken?"
        birthYear={birthYear}
      />
    </div>
  );
}

/**
 * BloodPressureRenderer
 * Renders systolic + diastolic inputs with an "I'm unable to take this measurement"
 * toggle and a date. When "unable" is active, both inputs are disabled.
 */
function BloodPressureRenderer({ questionId, value, onChange, birthYear }) {
  // Stabilize with useMemo so useCallback deps don't change every render
  const val = useMemo(() => value || {}, [value]);
  const unable = val.unable || false;
  const range = useRangeMessages('blood_pressure', val, questionId);
  const systolicRange = range('systolic');
  const diastolicRange = range('diastolic');

  const handleSystolic = useCallback((v) => {
    onChange({ ...val, systolic: v, unable: v != null ? false : val.unable });
  }, [val, onChange]);

  const handleDiastolic = useCallback((v) => {
    onChange({ ...val, diastolic: v, unable: v != null ? false : val.unable });
  }, [val, onChange]);

  const handleUnableToggle = useCallback(() => {
    const nowUnable = !unable;
    onChange({
      ...val,
      unable: nowUnable,
      systolic: nowUnable ? null : val.systolic,
      diastolic: nowUnable ? null : val.diastolic,
    });
  }, [val, unable, onChange]);

  const handleDate = useCallback((d) => onChange({ ...val, date: d, dateNotSure: false }), [val, onChange]);
  const handleDateNotSure = useCallback(() => {
    onChange({ ...val, dateNotSure: !val.dateNotSure, date: !val.dateNotSure ? null : val.date });
  }, [val, onChange]);

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
      <p className="text-sm font-medium text-gray-900">Please enter their blood pressure</p>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-center gap-1">
            <NumberInput
              id={`${questionId}_systolic`}
              value={val.systolic}
              onChange={handleSystolic}
              disabled={unable}
              placeholder="top"
              min={LIMITS.systolic[0]}
              max={LIMITS.systolic[1]}
              {...systolicRange.inputProps}
            />
            {/* <span className="text-xs text-gray-400">systolic</span> */}
          </div>
          <span className="text-sm text-gray-400">/</span>
          <div className="flex flex-col items-center gap-1">
            <NumberInput
              id={`${questionId}_diastolic`}
              value={val.diastolic}
              onChange={handleDiastolic}
              disabled={unable}
              placeholder="bottom"
              min={LIMITS.diastolic[0]}
              max={LIMITS.diastolic[1]}
              {...diastolicRange.inputProps}
            />
            {/* <span className="text-xs text-gray-400">diastolic</span> */}
          </div>
          <span className="text-xs text-gray-500">mmHg</span>
        </div>
        {/* "or" divider */}
        <span className="text-xs text-gray-400 select-none">or</span>
        {/* "Unable" pill */}
        <PillButton
          active={unable}
          onClick={handleUnableToggle}
          label="I'm unable to take this measurement"
          id={`${questionId}_unable`}
        />
      </div>
      <FieldError id={systolicRange.errorId} message={systolicRange.message} />
      <FieldError id={diastolicRange.errorId} message={diastolicRange.message} />
      {/* Date */}
      <MonthYearDateInput
        date={val.date}
        notSure={val.dateNotSure || false}
        onDateChange={handleDate}
        onNotSureToggle={handleDateNotSure}
        disabled={unable}
        idPrefix={`${questionId}_date`}
        dateLabel="When was this taken?"
        birthYear={birthYear}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PhysicalHealthInfoGroupField
 *
 * @param {Object} props
 * @param {Object} props.question   - The question object from schema (includes measurementType)
 * @param {Object} props.value      - Current answer value
 * @param {Function} props.onChange  - Callback when value changes: onChange(newValue)
 * @param {string}  [props.subjectDob] - CYP's date of birth (ISO string, e.g. "2018-07-05").
 *        Used to constrain year dropdowns so they don't offer dates before the child was born.
 */
export default function PhysicalHealthInfoGroupField({ question, value, onChange, subjectDob }) {
  // Normalize value to always be an object
  const currentValue = useMemo(() => {
    return typeof value === 'object' && value !== null ? value : {};
  }, [value]);

  const measurementType = question?.measurementType;
  const questionId = question?.questionId || 'phig';

  // Derive the CYP's birth year from the ISO DOB string (e.g. "2018-07-05" → 2018).
  // This constrains year dropdowns so the earliest selectable year is the child's birth year.
  const birthYear = useMemo(() => {
    if (!subjectDob) return null;
    const parsed = new Date(subjectDob);
    const y = parsed.getFullYear();
    return (Number.isFinite(y) && y > 1900) ? y : null;
  }, [subjectDob]);

  // Render the appropriate measurement-specific sub-component
  switch (measurementType) {
    case 'height_weight':
      return (
        <div className="space-y-4">
          <HeightWeightRenderer
            questionId={questionId}
            value={currentValue}
            onChange={onChange}
            birthYear={birthYear}
          />
        </div>
      );

    case 'head_circumference':
      return (
        <div className="space-y-4">
          <HeadCircumferenceRenderer
            questionId={questionId}
            value={currentValue}
            onChange={onChange}
            birthYear={birthYear}
          />
        </div>
      );

    case 'pulse_rate':
      return (
        <div className="space-y-4">
          <PulseRateRenderer
            questionId={questionId}
            value={currentValue}
            onChange={onChange}
            birthYear={birthYear}
          />
        </div>
      );

    case 'blood_pressure':
      return (
        <div className="space-y-4">
          <BloodPressureRenderer
            questionId={questionId}
            value={currentValue}
            onChange={onChange}
            birthYear={birthYear}
          />
        </div>
      );

    default:
      // Defensive: unknown measurementType — render nothing rather than crash
      return (
        <p className="text-sm text-red-500">
          Unknown measurement type: {measurementType}
        </p>
      );
  }
}
